import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { PRODUCT_NAME } from './constants';
interface Page {
  src: string;
  label: string;
  caption: string;
}
const pages: Page[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/gnis4agiYkTcHXdvJ7nNpK/Captura_de_pantalla_2026-09-08_a_la(s)_7.14.12_p._m..png',
  label: 'Visual Skill Progression',
  caption: 'Step-by-step handstand progression with goals and clear "pass when" readiness checkpoints.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kmQpoEmVrCLKDsQ1QQumW6/Captura_de_pantalla_2026-09-08_a_la(s)_7.13.45_p._m..png',
  label: 'Skill Development Map',
  caption: 'Front walkover pathway from prerequisite to skill, with key connections and a readiness checklist.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/n2aCBQt83onEd3SNn3R7hC/Captura_de_pantalla_2026-09-08_a_la(s)_7.13.26_p._m..png',
  label: 'Drill Development Sheet',
  caption: 'Split leap development curve with technical breakdowns for takeoff, legs, hips and landing.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3J1C8YfhoW9kQvvoy6LSgM/Captura_de_pantalla_2026-09-08_a_la(s)_7.12.19_p._m..png',
  label: 'Coaching Cues & Stations',
  caption: 'Jump and landing mechanics organized into a load → takeoff → land framework with coaching cues.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6avxmJ3w4LkW2KzZT5bgZQ/Captura_de_pantalla_2026-09-08_a_la(s)_7.12.00_p._m..png',
  label: 'Error Correction Guide',
  caption: 'Common landing errors with before/after visuals and the exact cue to fix each one fast.'
}];
export function LookInside() {
  const [index, setIndex] = useState(0);
  const go = (dir: number) => {
    setIndex(prev => (prev + dir + pages.length) % pages.length);
  };
  const current = pages[index];
  return <section className="py-8 px-4 bg-brand-cloud border-y border-brand-blue/20" data-id="element-118" data-mp="2zrgm0hh51hed">
      <div className="max-w-4xl mx-auto" data-id="element-119" data-mp="13o265iomt20r">
        <div className="text-center mb-5" data-id="element-120" data-mp="13n0sr7k583fv">
          <h2 className="text-2xl sm:text-3xl font-display font-extrabold text-brand-ink mb-1 uppercase leading-tight" data-id="element-121" data-mp="18mwn0f31ggi8">
            👀 Look Inside {PRODUCT_NAME}
          </h2>
          <p className="text-sm sm:text-base font-bold text-brand-blue uppercase tracking-wide" data-id="element-122" data-mp="1hg06p54a1plb">
            Don't Just Read What's Included. See What You'll Actually Use.
          </p>
        </div>

        <div className="bg-white border border-brand-line rounded-2xl shadow-lg overflow-hidden" data-id="element-123" data-mp="pokd2r4az2ll">
          <div className="relative bg-white" data-id="element-124" data-mp="tq5xqyn2wl6c">
            <a href="#pricing" className="block cursor-pointer" aria-label="Get the complete system" data-id="element-125" data-mp="tp4ck1ccmiaw">
              <img key={current.src} src={current.src} alt={`${current.label} — ${current.caption}`} className="w-full max-h-[70vh] sm:max-h-[560px] object-contain mx-auto" data-id="element-126" data-mp="256cf0h3fpocf" />
            </a>

            <button onClick={() => go(-1)} aria-label="Previous page" className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center shadow-lg hover:bg-brand-blue transition-colors" data-id="element-127" data-mp="2kaowx461wmt5">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-128" data-mp="21icxoqap21n8" />
            </button>
            <button onClick={() => go(1)} aria-label="Next page" className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center shadow-lg hover:bg-brand-blue transition-colors" data-id="element-129" data-mp="21g9wrb806o3q">
              <ChevronRightIcon className="w-5 h-5" data-id="element-130" data-mp="1nkoero06t4kt" />
            </button>

            <div className="absolute top-3 left-3 bg-brand-navy text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow" data-id="element-131" data-mp="3lxobj9j1wo2u">
              {current.label}
            </div>
            <div className="absolute top-3 right-3 bg-brand-gold text-brand-ink text-[11px] font-bold px-2.5 py-1 rounded-md shadow font-mono" data-id="element-132" data-mp="3p0caosmdv6km">
              {index + 1} / {pages.length}
            </div>
          </div>

          <div className="px-4 py-3 border-t border-brand-line bg-brand-cloud" data-id="element-133" data-mp="3g17gtzlro1es">
            <p className="text-sm text-brand-slate font-medium leading-snug text-center" data-id="element-134" data-mp="3jywpn0k7h028">
              {current.caption}
            </p>
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-4 flex-wrap" data-id="element-135" data-mp="3b0be3pd5ilay">
          {pages.map((p, i) => <button key={p.src} onClick={() => setIndex(i)} aria-label={`View ${p.label}`} aria-current={i === index} className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 bg-white transition-colors ${i === index ? 'border-brand-gold ring-2 ring-brand-blue/30' : 'border-brand-line hover:border-brand-blue'}`} data-id="element-136" data-mp="2wc6qq835lllu" data-mpi={i}>
              <img src={p.src} alt="" className="w-full h-full object-cover object-top" data-id="element-137" data-mp="23p6y0wk4dhr4" />
            </button>)}
        </div>

        <div className="mt-5" data-id="element-138" data-mp="2dmbo37h35xd7">
          <CTAButton className="max-w-md mx-auto" data-id="element-139" data-mp="2dm1ul9wu182r">
            🚀 YES! I WANT MY SYSTEM NOW
          </CTAButton>
        </div>
      </div>
    </section>;
}